import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Col, Form, Input, InputNumber, message, Modal, Row, Select, Space, Switch, Tag, Typography
} from 'antd';
import { LockOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { categoryApi, materialTypeApi } from './itemConfigApi';
import { uomApi } from '../uoms/uomApi';
import {
  MATERIAL_FAMILY_OPTIONS, MATERIAL_FAMILY_COLOR,
  ITEM_TYPE_OPTIONS, MEASUREMENT_MODE_OPTIONS, PRICING_METHOD_OPTIONS,
  INPUTTYPE_OPTIONS
} from './itemConfigConstants';
import '../../../../styles/erpModal.css';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Material Type modal (Stage 6B.8A-2A).
//
// Compact 760 px modal. Three sections:
//   1. Basic — code / name / family / default category + flags
//   2. Template Defaults — itemType / inputtype / measurementMode /
//      defaultUomCode / pricingMethod + 6 flag switches.
//   3. Control — sort order / remarks / active
//
// Operators never see the raw JSON. The UI captures fields, the form
// shape mirrors the JSONB structure 1:1, and the submit handler nests
// them into templateDefaults before posting.
// ─────────────────────────────────────────────────────────────────

const initialForm = () => ({
  materialTypeCode:  '',
  materialTypeName:  '',
  materialFamily:    'other',
  defaultCategoryId: null,
  isGlass:           false,
  isProfile:         false,
  isHardware:        false,
  isConsumable:      false,
  isJobwork:         false,
  // template_defaults — kept flat in the form for ergonomics;
  // re-nested at submit-time as templateDefaults.
  tdItemType:        null,
  tdInputtype:       null,
  tdMeasurementMode: null,
  tdDefaultUomCode:  null,
  tdPricingMethod:   null,
  tdIsStockItem:     true,
  tdIsPurchasable:   true,
  tdIsSellable:      true,
  tdIsManufacturable:false,
  tdIsConsumable:    false,
  tdIsJobworkItem:   false,
  sortOrder:         null,
  remarks:           '',
  isActive:          true
});

function unwrapApiError(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

function SectionTitle({ children }) {
  return (
    <div style={{
      margin: '2px 0 6px', borderBottom: '1px solid #f0f0f0', paddingBottom: 4,
      fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: '#6b7280'
    }}>{children}</div>
  );
}

function InlineHint({ icon, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 6,
      fontSize: 11.5, color: '#6b7280', lineHeight: 1.5,
      margin: '-2px 0 8px'
    }}>
      {icon ? <span style={{ marginTop: 1 }}>{icon}</span> : null}
      <span>{children}</span>
    </div>
  );
}

export default function MaterialTypeModal({ open, mode = 'add', record = null, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState([]);
  const [uoms, setUoms] = useState([]);
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const readOnly = isView;
  const isSystem = (isEdit || isView) && record?.isSystem === true;
  const codeLocked = readOnly || isSystem;

  useEffect(() => {
    if (!open) return;
    let abort = false;
    Promise.all([
      categoryApi.lookup({ limit: 200 }),
      uomApi.lookup({ limit: 200 })
    ]).then(([cats, us]) => {
      if (abort) return;
      setCategories(cats || []);
      setUoms(us || []);
    }).catch(() => {});
    return () => { abort = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (mode === 'add' || !record) {
      form.resetFields();
      form.setFieldsValue(initialForm());
    } else {
      const td = record.templateDefaults || {};
      form.setFieldsValue({
        materialTypeCode:  record.materialTypeCode || '',
        materialTypeName:  record.materialTypeName || '',
        materialFamily:    record.materialFamily || 'other',
        defaultCategoryId: record.defaultCategoryId || null,
        isGlass:           record.isGlass === true,
        isProfile:         record.isProfile === true,
        isHardware:        record.isHardware === true,
        isConsumable:      record.isConsumable === true,
        isJobwork:         record.isJobwork === true,
        tdItemType:        td.itemType || null,
        tdInputtype:       td.inputtype || null,
        tdMeasurementMode: td.measurementMode || null,
        tdDefaultUomCode:  td.defaultUomCode || null,
        tdPricingMethod:   td.pricingMethod || null,
        tdIsStockItem:     td.isStockItem !== false,
        tdIsPurchasable:   td.isPurchasable !== false,
        tdIsSellable:      td.isSellable !== false,
        tdIsManufacturable:td.isManufacturable === true,
        tdIsConsumable:    td.isConsumable === true,
        tdIsJobworkItem:   td.isJobworkItem === true,
        sortOrder:         Number.isFinite(record.sortOrder) ? record.sortOrder : null,
        remarks:           record.remarks || '',
        isActive:          record.isActive !== false
      });
    }
  }, [open, mode, record, form]);

  const onSubmit = useCallback(async () => {
    if (readOnly) return onClose?.();
    let values;
    try {
      values = await form.validateFields();
    } catch (_e) { return; }
    setBusy(true);
    try {
      const templateDefaults = {
        ...(values.tdItemType        ? { itemType:        values.tdItemType } : {}),
        ...(values.tdInputtype       ? { inputtype:       values.tdInputtype } : {}),
        ...(values.tdMeasurementMode ? { measurementMode: values.tdMeasurementMode } : {}),
        ...(values.tdDefaultUomCode  ? { defaultUomCode:  values.tdDefaultUomCode } : {}),
        ...(values.tdPricingMethod   ? { pricingMethod:   values.tdPricingMethod } : {}),
        isStockItem:      values.tdIsStockItem === true,
        isPurchasable:    values.tdIsPurchasable === true,
        isSellable:       values.tdIsSellable === true,
        isManufacturable: values.tdIsManufacturable === true,
        isConsumable:     values.tdIsConsumable === true,
        isJobworkItem:    values.tdIsJobworkItem === true
      };
      const basePayload = {
        materialTypeName:  values.materialTypeName,
        materialFamily:    values.materialFamily,
        defaultCategoryId: values.defaultCategoryId || null,
        templateDefaults,
        isGlass:           values.isGlass === true,
        isProfile:         values.isProfile === true,
        isHardware:        values.isHardware === true,
        isConsumable:      values.isConsumable === true,
        isJobwork:         values.isJobwork === true,
        sortOrder:         values.sortOrder,
        remarks:           values.remarks || null,
        isActive:          values.isActive !== false
      };
      if (isEdit && record?.id) {
        // System rows: server rejects materialTypeCode; we still gate
        // the wire-up on isSystem so a stray submit can't trip it.
        const payload = isSystem ? basePayload : { ...basePayload, materialTypeCode: values.materialTypeCode };
        await materialTypeApi.update(record.id, payload);
        message.success('Material type updated.');
      } else {
        await materialTypeApi.create({
          ...basePayload,
          materialTypeCode: String(values.materialTypeCode || '').trim().toUpperCase()
        });
        message.success('Material type created.');
      }
      onSaved?.();
    } catch (e) {
      message.error(unwrapApiError(e, isEdit ? 'Failed to update material type.' : 'Failed to create material type.'));
    } finally {
      setBusy(false);
    }
  }, [form, isEdit, readOnly, isSystem, record, onClose, onSaved]);

  const titleNode = useMemo(() => {
    const label = mode === 'add' ? 'New Material Type' : `Material Type — ${record?.materialTypeCode || ''}`;
    const chips = [];
    if (record?.isSystem) chips.push(<Tag key="sys" icon={<LockOutlined />} color="default" style={{ marginInlineEnd: 0 }}>System</Tag>);
    if (record) chips.push(
      <Tag key="active" color={record.isActive === false ? 'default' : 'success'} style={{ marginInlineEnd: 0 }}>
        {record.isActive === false ? 'Inactive' : 'Active'}
      </Tag>);
    if (record?.materialFamily) chips.push(
      <Tag key="fam" color={MATERIAL_FAMILY_COLOR[record.materialFamily] || 'default'} style={{ marginInlineEnd: 0 }}>
        {record.materialFamily}
      </Tag>);
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 28 }}>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{label}</span>
        {chips.length ? <Space size={6} wrap>{chips}</Space> : null}
      </div>
    );
  }, [mode, record]);

  const categoryOptions = useMemo(() => categories.map((c) => ({
    value: c.id,
    label: (
      <span>
        <code style={{ fontWeight: 600 }}>{c.categoryCode}</code>
        <span style={{ color: '#6b7280', marginLeft: 6 }}>{c.categoryName}</span>
      </span>
    )
  })), [categories]);

  const uomOptions = useMemo(() => uoms.map((u) => ({
    value: u.uomCode,
    label: (
      <span>
        <code style={{ fontWeight: 600 }}>{u.uomCode}</code>
        <span style={{ color: '#6b7280', marginLeft: 6 }}>{u.uomName}</span>
      </span>
    )
  })), [uoms]);

  return (
    <Modal
      open={open}
      title={titleNode}
      onCancel={onClose}
      onOk={readOnly ? onClose : onSubmit}
      okText={readOnly ? 'Close' : isEdit ? 'Save' : 'Create'}
      okButtonProps={{ loading: busy }}
      cancelButtonProps={{ disabled: busy }}
      width={820}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-enterprise-modal"
    >
      {isSystem ? (
        <InlineHint icon={<LockOutlined style={{ color: '#6b7280' }} />}>
          Seeded system material type. Code is locked; family / category /
          template defaults / flags remain editable so admin can tune the
          Product Creation Studio wizard.
        </InlineHint>
      ) : null}

      <Form form={form} layout="vertical" size="middle" requiredMark="optional"
        disabled={readOnly} className="item-config-compact-form erp-enterprise-form">
        <style>{`
          .item-config-compact-form .ant-form-item { margin-bottom: 10px; }
          .item-config-compact-form .ant-form-item-label { padding-bottom: 2px; }
          .item-config-compact-form .ant-form-item-label > label { font-size: 12px; color: #475569; height: 22px; }
          .item-config-compact-form .ant-input,
          .item-config-compact-form .ant-input-number-input,
          .item-config-compact-form .ant-select-selector { font-size: 13px; }
          .item-config-compact-form .ant-form-item-explain { font-size: 11.5px; min-height: 16px; }
        `}</style>

        <SectionTitle>Basic</SectionTitle>
        <Row gutter={[10, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item label="Material Code" name="materialTypeCode"
              rules={[
                { required: true, message: 'Required' },
                { max: 50, message: 'Max 50 chars' },
                { pattern: /^[A-Za-z0-9_-]+$/, message: 'A-Z, 0-9, _, -' }
              ]}>
              <Input
                maxLength={50}
                disabled={codeLocked}
                placeholder="e.g. TOUGHENED_GLASS"
                suffix={codeLocked ? <LockOutlined style={{ color: '#9ca3af' }} /> : null}
                onChange={(e) => form.setFieldsValue({
                  materialTypeCode: e.target.value.toUpperCase()
                })}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item label="Material Name" name="materialTypeName"
              rules={[
                { required: true, message: 'Required' },
                { max: 150, message: 'Max 150 chars' }
              ]}>
              <Input maxLength={150} placeholder="e.g. Toughened Glass" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Family" name="materialFamily"
              rules={[{ required: true, message: 'Required' }]}>
              <Select options={MATERIAL_FAMILY_OPTIONS.map((o) => ({
                value: o.value,
                label: <Tag color={MATERIAL_FAMILY_COLOR[o.value]} style={{ marginInlineEnd: 0 }}>{o.label}</Tag>
              }))} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item label="Default Category" name="defaultCategoryId">
              <Select
                allowClear
                showSearch
                placeholder="Optional"
                options={categoryOptions}
                filterOption={(input, opt) => {
                  const txt = categories.find((c) => c.id === opt?.value);
                  if (!txt) return false;
                  return (txt.categoryCode + ' ' + txt.categoryName).toLowerCase().includes(input.toLowerCase());
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <SectionTitle>Flags</SectionTitle>
        <Row gutter={[10, 0]}>
          <Col xs={12} sm={5}>
            <Form.Item label="Glass" name="isGlass" valuePropName="checked"><Switch /></Form.Item>
          </Col>
          <Col xs={12} sm={5}>
            <Form.Item label="Profile" name="isProfile" valuePropName="checked"><Switch /></Form.Item>
          </Col>
          <Col xs={12} sm={5}>
            <Form.Item label="Hardware" name="isHardware" valuePropName="checked"><Switch /></Form.Item>
          </Col>
          <Col xs={12} sm={5}>
            <Form.Item label="Consumable" name="isConsumable" valuePropName="checked"><Switch /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item label="Jobwork" name="isJobwork" valuePropName="checked"><Switch /></Form.Item>
          </Col>
        </Row>

        <SectionTitle>Template Defaults</SectionTitle>
        <InlineHint icon={<InfoCircleOutlined style={{ color: '#6b7280' }} />}>
          These defaults seed the Product Creation Studio wizard when an
          operator picks this material type. Item Master rows can still
          override them per-item later.
        </InlineHint>
        <Row gutter={[10, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item label="Item Type" name="tdItemType">
              <Select allowClear options={ITEM_TYPE_OPTIONS} placeholder="Optional" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Input Type" name="tdInputtype">
              <Select allowClear options={INPUTTYPE_OPTIONS} placeholder="Optional" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Measurement Mode" name="tdMeasurementMode">
              <Select allowClear options={MEASUREMENT_MODE_OPTIONS} placeholder="Optional" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Default UOM" name="tdDefaultUomCode"
              tooltip="Picker reads from UOM Master. Saved as string code, not id.">
              <Select
                allowClear
                showSearch
                options={uomOptions}
                placeholder="Optional"
                filterOption={(input, opt) => {
                  const txt = uoms.find((u) => u.uomCode === opt?.value);
                  if (!txt) return false;
                  return (txt.uomCode + ' ' + txt.uomName).toLowerCase().includes(input.toLowerCase());
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Pricing Method" name="tdPricingMethod">
              <Select allowClear options={PRICING_METHOD_OPTIONS} placeholder="Optional" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[10, 0]}>
          <Col xs={12} sm={4}>
            <Form.Item label="Stock Item" name="tdIsStockItem" valuePropName="checked"><Switch /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item label="Purchasable" name="tdIsPurchasable" valuePropName="checked"><Switch /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item label="Sellable" name="tdIsSellable" valuePropName="checked"><Switch /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item label="Manufacturable" name="tdIsManufacturable" valuePropName="checked"><Switch /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item label="Consumable Item" name="tdIsConsumable" valuePropName="checked"><Switch /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item label="Jobwork Item" name="tdIsJobworkItem" valuePropName="checked"><Switch /></Form.Item>
          </Col>
        </Row>

        <SectionTitle>Control</SectionTitle>
        <Row gutter={[10, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item label="Sort Order" name="sortOrder">
              <InputNumber min={0} max={999999} precision={0}
                style={{ width: '100%' }} placeholder="e.g. 10" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label="Remarks" name="remarks" style={{ marginBottom: 0 }}>
              <Input.TextArea rows={2} maxLength={2000} placeholder="Optional notes" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
